import { z } from 'zod';

import { PublicOrgSchema } from './orgs';
import { ClientIdSchema } from './requests';
import {
  AgeRangeSchema,
  ContactOutcomeSchema,
  FollowUpKindSchema,
  FollowUpStatusSchema,
  IdSchema,
  IsoDateSchema,
  JourneyStageSchema,
  RegistrationSchema,
  VisitorSchema,
  VisitorSourceSchema,
  VisitorVisitSchema,
} from './resources';

/**
 * Visitantes e integração (Membresia): a ficha opcional, com consentimento, os
 * acompanhamentos e o link público. Só quem gerencia visitantes (`manageVisitors`) usa as
 * rotas da equipe; as regras são as de `@escala/domain/visitors`.
 */

const NoteSchema = z.string().max(140);

/** Os dados da ficha (criar e editar). Os erros de negócio vêm de `visitorErrors`. */
export const VisitorInputSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    phone: z.string().max(20).meta({ description: 'Com ou sem máscara; gravado só com os dígitos.' }),
    email: z.string().max(254),
    ageRange: AgeRangeSchema,
    district: z.string().max(80),
    source: VisitorSourceSchema,
    invitedById: IdSchema.nullable(),
    wantsContact: z.boolean(),
    wantsMembership: z.boolean(),
    note: NoteSchema,
    consent: z.literal(true).meta({ description: 'A pessoa autorizou o registro e o contato.' }),
    visit: VisitorVisitSchema.meta({ description: 'A primeira visita (só vale na criação).' }),
  })
  .meta({ id: 'VisitorInput' });

export const VisitorCreateSchema = VisitorInputSchema.extend({ id: ClientIdSchema }).meta({
  id: 'VisitorCreate',
});

export const VisitRequestSchema = VisitorVisitSchema.meta({
  id: 'VisitRequest',
  description: 'Registrar visita.',
});

export const VisitorStageRequestSchema = z
  .object({ stage: z.string().min(1).max(40) })
  .meta({ id: 'VisitorStageRequest', description: 'Mover a ficha para outra etapa da jornada.' });

export const VisitorOwnerRequestSchema = z
  .object({ ownerId: IdSchema.nullable() })
  .meta({ id: 'VisitorOwnerRequest' });

export const FollowUpCreateSchema = z
  .object({
    id: ClientIdSchema,
    kind: FollowUpKindSchema,
    ownerId: IdSchema.nullable(),
    due: IsoDateSchema,
    note: NoteSchema,
  })
  .meta({ id: 'FollowUpCreate' });

export const FollowUpPatchSchema = z
  .object({
    kind: FollowUpKindSchema,
    ownerId: IdSchema.nullable(),
    due: IsoDateSchema,
    note: NoteSchema,
    status: FollowUpStatusSchema,
  })
  .partial()
  .meta({ id: 'FollowUpPatch' });

export const VisitorContactRequestSchema = z
  .object({ id: ClientIdSchema, kind: FollowUpKindSchema, outcome: ContactOutcomeSchema })
  .meta({ id: 'VisitorContactRequest', description: 'Registrar um contato feito com o visitante.' });

export const VisitorReferralRequestSchema = z
  .object({
    registrationId: ClientIdSchema.unwrap().meta({ description: 'Id da solicitação de cadastro que nasce.' }),
  })
  .meta({ id: 'VisitorReferralRequest', description: 'Encaminhar a ficha para o cadastro de membro.' });

export const VisitorReferralSchema = z
  .object({ visitor: VisitorSchema, registration: RegistrationSchema })
  .meta({ id: 'VisitorReferral' });

export const VisitorLinkRequestSchema = z
  .object({ registrationId: IdSchema })
  .meta({ id: 'VisitorLinkRequest', description: 'Ligar à ficha o cadastro que a pessoa fez pelo link.' });

export const VisitorSettingsSchema = z
  .object({
    visitorJourney: z.array(JourneyStageSchema).min(3).max(12),
    visitorRetentionMonths: z.number().int().min(3).max(24),
  })
  .meta({ id: 'VisitorSettings', description: 'A jornada de integração e o prazo de guarda das fichas.' });

export const VisitorLinkPatchSchema = z
  .object({ active: z.boolean() })
  .meta({ id: 'VisitorLinkPatch', description: 'Pausar ou retomar o link da ficha de visitante.' });

// ===== Formulário público =====

export const VisitorFormSchema = z
  .object({
    org: PublicOrgSchema.omit({ registrationToken: true }),
    open: z.boolean().meta({ description: 'Falso: link pausado.' }),
  })
  .meta({ id: 'VisitorForm', description: 'O que o formulário público da ficha precisa para abrir.' });

export const VisitorSubmitSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    phone: z.string().max(20),
    email: z.string().max(254),
    ageRange: AgeRangeSchema,
    district: z.string().max(80),
    source: VisitorSourceSchema,
    firstTime: z.boolean().meta({ description: 'Primeira vez na igreja (só informativo).' }),
    wantsContact: z.boolean(),
    wantsMembership: z.boolean(),
    adult: z.literal(true).meta({ description: 'O formulário público é só para quem tem 18 anos ou mais.' }),
    consent: z.literal(true).meta({ description: 'Autorização da LGPD — obrigatória para enviar.' }),
  })
  .meta({ id: 'VisitorSubmit' });

export type VisitorInput = z.infer<typeof VisitorInputSchema>;
export type VisitorCreate = z.infer<typeof VisitorCreateSchema>;
export type FollowUpCreate = z.infer<typeof FollowUpCreateSchema>;
export type FollowUpPatch = z.infer<typeof FollowUpPatchSchema>;
export type VisitorContactRequest = z.infer<typeof VisitorContactRequestSchema>;
export type VisitorSettings = z.infer<typeof VisitorSettingsSchema>;
export type VisitorForm = z.infer<typeof VisitorFormSchema>;
export type VisitorSubmit = z.infer<typeof VisitorSubmitSchema>;
